/**
 * API client for DSOK system settings (global admin settings; customer 1 only).
 * GET returns full settings JSON; PUT expects full settings JSON (backend persists as-is).
 *
 * DÔLEŽITÉ: systémové nastavenia sú GLOBÁLNE (nie per-DSOK-zákazník), preto cieľový
 * backend NEsledujeme cez getBEUrl (per-customer beDomain), ale podľa HOSTITEĽA AdminUI,
 * kde je admin prihlásený:
 *   - localhost / 127.0.0.1        → http://localhost:8080
 *   - *staging* host               → https://staging.gastrokiosk.online
 *   - inak (napr. dsok.datavision-online.eu) → https://be.gastrokiosk.online (produkcia)
 */
import jwtAuthKioskAxios from "../auth/jwtAuthKiosk";
import { deepClone } from "app/utils/objectHelpers";

/** Cieľový backend systémových nastavení podľa hostiteľa AdminUI. */
function systemSettingsBaseURL() {
  let host = "";
  try {
    host = (window?.location?.hostname || "").toLowerCase();
  } catch (e) {
    host = "";
  }
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8080";
  if (host.includes("staging")) return "https://staging.gastrokiosk.online";
  return "https://be.gastrokiosk.online";
}

/** Per-request axios config, ktorý prebije default baseURL cieľom podľa hostiteľa. */
export function systemSettingsRequestConfig() {
  return { baseURL: `${systemSettingsBaseURL()}/` };
}

/**
 * GET /dsok/system-settings
 * @returns {Promise<Object>} Full system settings object (top-level keys e.g. colors, translations, ...)
 */
export async function getDsokSystemSettings() {
  const { data } = await jwtAuthKioskAxios.get(
    "/dsok/system-settings",
    systemSettingsRequestConfig(),
  );
  return data?.data != null ? data.data : data;
}

/**
 * PUT /dsok/system-settings
 * @param {Object} payload - Full system settings object to persist
 * @returns {Promise<Object>} Response from server (use as new source of truth if backend returns updated settings)
 */
export async function saveDsokSystemSettings(payload) {
  const { data } = await jwtAuthKioskAxios.put(
    "/dsok/system-settings",
    payload,
    systemSettingsRequestConfig(),
  );
  return data?.data != null ? data.data : data;
}

/**
 * Build full settings payload for PUT by cloning original and overwriting only the given dirty sections from draft.
 * Use so that only edited sections (e.g. colors or translations) change; all other keys stay as in original.
 * @param {Object} originalSettings - Last fetched full settings (source of truth for untouched parts)
 * @param {Object} draftSettings - Draft with edited sections
 * @param {{ colors?: boolean, translations?: boolean }} dirtySections - Which top-level sections to take from draft
 * @returns {Object} New object safe to send to saveDsokSystemSettings
 */
export function buildPartialSettingsPayload(originalSettings, draftSettings, dirtySections) {
  const payload = deepClone(originalSettings);
  if (!payload.newCustomerDefaults) payload.newCustomerDefaults = {};
  if (dirtySections.colors) {
    payload.newCustomerDefaults.colors = deepClone(
      draftSettings?.newCustomerDefaults?.colors ?? {}
    );
  }
  if (dirtySections.translations) {
    payload.newCustomerDefaults.translations = deepClone(
      draftSettings?.newCustomerDefaults?.translations ?? {}
    );
  }
  return payload;
}
